import React, { useEffect, useState } from 'react';
import { Compass } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { fetchFlagshipPrograms, fetchAnnouncements, NEED_ORDER } from '../../lib/programsApi';
import { ProgramCard } from './ProgramCard';

const SERIF = { fontFamily: "'Playfair Display', serif" };

const NeedChip = ({ p, active, onPick }) => (
  <button
    type="button"
    onClick={() => onPick(p)}
    className={`text-left rounded-2xl px-4 py-3 border transition-[background-color,border-color,transform] duration-300 active:scale-[0.98] ${active ? 'bg-[#0f1e33] text-[#f9f4ea] border-[#0f1e33]' : 'bg-white/70 text-[#0f1e33] border-[#e3d2a8]/60 hover:border-[#D6AD60]'}`}
    data-testid={`need-chip-${p.slug}`}
  >
    <span className="block text-sm font-semibold" style={SERIF}>{p.needLabel}</span>
    <span className={`block text-[11px] mt-0.5 ${active ? 'text-[#e3d2a8]' : 'text-[#7a9b8e]'}`}>{p.title}</span>
  </button>
);

export const FlagshipProgramsSection = () => {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [picked, setPicked] = useState(null);
  const [ribbons, setRibbons] = useState({});

  useEffect(() => {
    fetchFlagshipPrograms().then((d) => setItems(d.items || [])).catch(() => setItems([]));
    fetchAnnouncements('programs').then((d) => setRibbons(Object.fromEntries((d.ribbons || []).map((r) => [r.programSlug, r.label])))).catch(() => {});
  }, [user?.id]);

  const onPick = (p) => {
    setPicked(p.slug);
    document.getElementById(`flagship-${p.slug}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  if (items && !items.length) return null;
  const ordered = items ? [...items].sort((a, b) => NEED_ORDER.indexOf(a.slug) - NEED_ORDER.indexOf(b.slug)) : [];

  return (
    <section className="px-5 pb-8" data-testid="flagship-programs-section">
      <div className="rounded-3xl bg-[#0f1e33] text-[#f9f4ea] p-5 mb-6 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#D6AD60]/15 blur-3xl" />
        <div className="flex items-center gap-2 mb-1 text-[#e3d2a8]">
          <Compass className="w-4 h-4" />
          <p className="text-[10px] uppercase tracking-[0.3em]">Start Your Journey</p>
        </div>
        <h2 className="text-lg font-bold mb-4" style={SERIF}>Choose what you need right now</h2>
        {!items ? (
          <div className="grid grid-cols-2 gap-2">{[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-14 rounded-2xl bg-white/10 animate-pulse" />)}</div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {ordered.map((p) => <NeedChip key={p.slug} p={p} active={picked === p.slug} onPick={onPick} />)}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-5">
        {!items
          ? [0, 1].map((i) => <div key={i} className="h-72 rounded-3xl bg-[#efe3ce] animate-pulse" />)
          : ordered.map((p) => <ProgramCard key={p.slug} p={p} highlighted={picked === p.slug} ribbon={ribbons[p.slug]} />)}
      </div>
    </section>
  );
};
